// src/pages/Settings.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { GlobalStyles } from "../styles/globalStyles";
import { Moon, Sun, Globe, Save } from "lucide-react";
import toast from "react-hot-toast";

const Settings: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { mode } = useSelector((state: RootState) => state.theme);
  const [settings, setSettings] = useState({
    theme: mode,
    currency: localStorage.getItem("currency") || "USD",
    language: "en",
  });

  useEffect(() => {
    setSettings((prev) => ({ ...prev, theme: mode }));
  }, [mode]);

  const handleSaveSettings = () => {
    localStorage.setItem("currency", settings.currency);
    toast.success("Settings saved successfully");
  };

  if (!user) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <h1 className={GlobalStyles.heading1}>Settings</h1>
          <p className={GlobalStyles.bodyText + " mt-2"}>
            Customize your application preferences
          </p>
        </div>

        <div className={GlobalStyles.card}>
          {/* Theme Settings */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={GlobalStyles.heading3}>Theme</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  Choose your preferred theme
                </p>
              </div>
              {settings.theme === "dark" ? (
                <Moon className="h-6 w-6 text-primary" />
              ) : (
                <Sun className="h-6 w-6 text-primary" />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setSettings({ ...settings, theme: "light" })}
                className={`flex items-center justify-center p-4 border rounded-lg transition-colors ${
                  settings.theme === "light"
                    ? "border-primary bg-primary/10"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <Sun className="h-6 w-6 mr-2" />
                <span>Light Mode</span>
              </button>
              <button
                onClick={() => setSettings({ ...settings, theme: "dark" })}
                className={`flex items-center justify-center p-4 border rounded-lg transition-colors ${
                  settings.theme === "dark"
                    ? "border-primary bg-primary/10"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <Moon className="h-6 w-6 mr-2" />
                <span>Dark Mode</span>
              </button>
            </div>
          </div>

          {/* Currency Settings */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={GlobalStyles.heading3}>Currency</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  Select your preferred currency
                </p>
              </div>
              <Globe className="h-6 w-6 text-primary" />
            </div>

            <div className="mb-4">
              <label className={GlobalStyles.inputLabel}>Currency</label>
              <select
                value={settings.currency}
                onChange={(e) =>
                  setSettings({ ...settings, currency: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="USD">US Dollar (USD)</option>
                <option value="EUR">Euro (EUR)</option>
                <option value="GBP">British Pound (GBP)</option>
                <option value="JPY">Japanese Yen (JPY)</option>
                <option value="CAD">Canadian Dollar (CAD)</option>
                <option value="AUD">Australian Dollar (AUD)</option>
                <option value="BTC">Bitcoin (BTC)</option>
                <option value="ETH">Ethereum (ETH)</option>
              </select>
            </div>
          </div>

          {/* Language Settings */}
          <div className="mb-8">
            <div className="mb-4">
              <label className={GlobalStyles.inputLabel}>Language</label>
              <select
                value={settings.language}
                onChange={(e) =>
                  setSettings({ ...settings, language: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese</option>
                <option value="ja">Japanese</option>
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>

        {/* Info */}
        <div className={`${GlobalStyles.card} mt-6`}>
          <h3 className={GlobalStyles.heading3}>Note</h3>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Theme changes are applied globally. Currency settings affect how
            amounts are displayed throughout the application.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
